import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { ScrollArea } from '@/shared/ui/scroll-area';
import {
  BookOpen,
  Bookmark,
  Clock,
  Play,
  Youtube,
  Sprout
} from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useKnowledgeAssets, KnowledgeAsset } from '@/shared/hooks/useKnowledgeAssets';
import { LoadingState } from '@/components/interactions/LoadingState';
import { EmptyState } from '@/components/interactions/EmptyState';
import { useAuth } from '@/features/auth/components/AuthContext';
import { isTrainingAsset } from '@/features/help/lib/trainingVideos';

interface KnowledgeBaseProps {
  onAskAgent?: (question: string) => void;
}

function formatDuration(seconds: number | null): string {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function KnowledgeBase({ onAskAgent }: KnowledgeBaseProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: allAssets, isLoading } = useKnowledgeAssets();
  const [bookmarked, setBookmarked] = useState<Set<string>>(new Set());

  // TVTool B7a (spec §9.2.5): the training videos belong to the Help screen — never shown twice here.
  const assets = (allAssets ?? []).filter((a) => !isTrainingAsset(a));
  const visibleAssets = assets;

  const handleContentClick = (asset: KnowledgeAsset) => {
    navigate(`/knowledge/${asset.id}`);
  };

  const toggleBookmark = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setBookmarked(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  if (isLoading) {
    return <LoadingState message={t('knowledge.loading', 'Loading knowledge base...')} />;
  }

  // Empty state when no assets match user's country/crops
  if (!assets || assets.length === 0) {
    const countryName = user?.countryCode || '';
    return (
      <div className="flex flex-col h-full bg-background">
        <EmptyState
          icon={Sprout}
          title={t('knowledge.comingSoonTitle', 'Content Coming Soon')}
          description={t(
            'knowledge.comingSoonDescription',
            `Knowledge content for ${countryName} farming is coming soon. Check back later for guides tailored to your crops and region.`,
            { country: countryName }
          )}
          action={
            onAskAgent
              ? {
                  label: t('agent.askInstead', 'Ask AgriAgent'),
                  onClick: () => onAskAgent('What farming tips do you have for my crops?'),
                }
              : undefined
          }
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background">
      <div className="px-4 py-3 border-b border-border">
        <h2 className="text-lg font-semibold">
          {t('knowledge.title', { defaultValue: 'Knowledge Base' })}
        </h2>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4">
          {visibleAssets.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>{t('knowledge.noContent')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {visibleAssets.map(asset => {
                const isYouTube = asset.type === 'youtube_video';
                return (
                  <Card
                    key={asset.id}
                    className="relative overflow-hidden cursor-pointer active:scale-[0.98] transition-transform rounded-xl h-[180px] flex flex-col"
                    onClick={() => handleContentClick(asset)}
                  >
                    {/* Thumbnail - 70% */}
                    <div className="relative h-[70%] overflow-hidden">
                      <img
                        src={asset.thumbnail_url || 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400'}
                        alt={asset.title_local || asset.title}
                        className="w-full h-full object-cover"
                      />

                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <div className={cn(
                          "rounded-full p-3",
                          isYouTube ? "bg-red-600" : "bg-primary"
                        )}>
                          {isYouTube ? (
                            <Youtube className="h-6 w-6 text-white" />
                          ) : (
                            <Play className="h-6 w-6 text-primary-foreground fill-current" />
                          )}
                        </div>
                      </div>

                      {/* Duration Badge */}
                      {asset.media_duration_seconds && (
                        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDuration(asset.media_duration_seconds)}
                          </div>
                        </div>
                      )}

                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-2 left-2 h-8 w-8 bg-white/90 hover:bg-white"
                        onClick={(e) => toggleBookmark(asset.id, e)}
                      >
                        <Bookmark
                          className={cn(
                            "h-4 w-4",
                            bookmarked.has(asset.id) && "fill-current text-yellow-500"
                          )}
                        />
                      </Button>

                      <div className={cn(
                        "absolute top-2 left-12 text-xs px-2 py-1 rounded-full text-white flex items-center gap-1",
                        isYouTube ? "bg-red-600" : "bg-blue-500"
                      )}>
                        {isYouTube ? <Youtube className="h-3 w-3" /> : <Play className="h-3 w-3 fill-current" />}
                      </div>
                    </div>

                    {/* Title Bar - 30% */}
                    <div className="h-[30%] flex items-center px-3 bg-card">
                      <h3 className="font-bold text-sm leading-tight line-clamp-2 text-foreground">
                        {asset.title_local || asset.title}
                      </h3>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
